import { useEffect, useState } from 'react';
import { ArrowLeft, ArrowRight, MailCheck, ShieldCheck } from 'lucide-react';
import { ApiClientError, LogoMark, Spinner } from '@riskpay/ui-components';
import { authApi, useAuth } from '../lib/client';
import { EmailVerification } from './EmailVerification';

/* ==========================================================================
 *  Sign in
 *
 *  Password, then an emailed passcode. The server decides which step comes
 *  next — a token straight back means the account already clears TOTP (a
 *  strictly stronger factor) or BYPASS_OTP is on; an `otp_required` response
 *  means the second step is due.
 *
 *  The authenticator field only appears if the API asks for it. Showing an
 *  empty code box to the majority who have not enrolled reads as an error.
 * ========================================================================== */

interface Session {
  access_token: string;
  refresh_token: string;
  user: { id: string; email: string; role: string; merchant_id?: string; totp_enabled?: boolean };
}

interface LoginResponse extends Partial<Session> {
  otp_required?: boolean;
  /** The password was right, but the address was never confirmed. */
  verification_required?: boolean;
  email?: string;
  expires_in?: number;
  resend_after?: number;
  dev_code?: string;
}

export function SignIn({ onNavigate }: { onNavigate: (to: 'signup' | 'forgot') => void }) {
  const signIn = useAuth((s) => s.signIn);

  const [step, setStep] = useState<'credentials' | 'passcode' | 'verify'>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [code, setCode] = useState('');
  const [devCode, setDevCode] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Countdown on the resend control, so it is obvious when it becomes usable.
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = window.setInterval(() => setResendIn((n) => Math.max(0, n - 1)), 1000);
    return () => window.clearInterval(t);
  }, [resendIn]);

  function establish(session: Session) {
    signIn({ accessToken: session.access_token, user: session.user, mode: 'TEST' });
  }

  async function submitCredentials(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      const result = await authApi.post<LoginResponse>('/merchant/login', {
        email,
        password,
        ...(totp ? { totp } : {}),
      });

      /*
       * Checked before otp_required, because an unconfirmed address is a
       * different problem with a different endpoint. Sending someone to the
       * sign-in passcode screen would have them type a code that /otp/verify
       * cannot accept, and the error would blame the code.
       */
      if (result.verification_required) {
        setDevCode(result.dev_code ?? null);
        setResendIn(result.resend_after ?? 60);
        setStep('verify');
        return;
      }

      if (result.otp_required) {
        setDevCode(result.dev_code ?? null);
        setResendIn(result.resend_after ?? 60);
        setStep('passcode');
        return;
      }

      if (result.access_token && result.user) establish(result as Session);
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'two_factor_required') {
        setNeedsTotp(true);
        setError('Enter the 6-digit code from your authenticator app.');
      } else {
        setError(err instanceof ApiClientError ? err.message : 'Sign-in failed. Try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function submitPasscode(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await authApi.post<Session>('/otp/verify', { email, code });
      establish(result);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'That code did not work.');
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setBusy(true);
    setError(null);
    try {
      const result = await authApi.post<{ dev_code?: string; resend_after?: number }>('/otp/request', { email });
      setDevCode(result.dev_code ?? null);
      setResendIn(result.resend_after ?? 60);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not resend the code.');
    } finally {
      setBusy(false);
    }
  }

  /*
   * Rendered outside the panel below because EmailVerification brings its own,
   * and because confirming can end in the one-time secret — a screen that has
   * nothing to do with signing in and should not be squeezed into its shell.
   */
  if (step === 'verify') {
    return (
      <div className="flex min-h-screen items-center justify-center px-5 py-12">
        <div className="w-full max-w-panel">
          <div className="mb-8 flex items-center gap-2.5">
            <LogoMark size={52} />
            <div className="leading-none">
              <p className="text-lg font-semibold tracking-tight">RiskPay</p>
              <p className="mt-1.5 text-sm text-content-subtle">Merchant Portal</p>
            </div>
          </div>

          <EmailVerification
            email={email}
            resendAfter={resendIn}
            initialDevCode={devCode}
            onVerified={establish}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-5 py-12">
      <div className="w-full max-w-panel">
        <div className="mb-8 flex items-center gap-2.5">
          <LogoMark size={52} />
          <div className="leading-none">
            <p className="text-lg font-semibold tracking-tight">RiskPay</p>
            <p className="mt-1.5 text-sm text-content-subtle">Merchant Portal</p>
          </div>
        </div>

        <div className="panel p-6 sm:p-7">
          {step === 'credentials' ? (
            <>
              <h1 className="text-xl font-semibold tracking-tight">Sign in</h1>
              <p className="mt-1.5 text-sm text-content-muted">Access your dashboard, settlements and API keys.</p>

              <form onSubmit={submitCredentials} className="mt-6 space-y-4">
                <div>
                  <label htmlFor="email" className="label">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="username"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="label">
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input"
                  />
                </div>

                {needsTotp && (
                  <div className="animate-slide-down">
                    <label htmlFor="totp" className="label">
                      Authenticator code
                    </label>
                    <input
                      id="totp"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      pattern="\d{6}"
                      value={totp}
                      onChange={(e) => setTotp(e.target.value.replace(/\D/g, ''))}
                      className="input numeral tracking-[0.4em]"
                      autoFocus
                    />
                  </div>
                )}

                {error && (
                  <p
                    role="alert"
                    className="rounded-md border border-danger-ring bg-danger-soft px-3 py-2.5 text-xs leading-relaxed text-danger-text"
                  >
                    {error}
                  </p>
                )}

                <button type="submit" className="btn btn-primary btn-lg w-full" disabled={busy}>
                  {busy ? <Spinner /> : <ArrowRight className="h-4 w-4" aria-hidden="true" />}
                  {busy ? 'Signing in' : 'Continue'}
                </button>
              </form>

              <div className="mt-6 flex items-center justify-between border-t border-line pt-4 text-sm">
                <button
                  type="button"
                  onClick={() => onNavigate('forgot')}
                  className="text-content-muted transition-colors hover:text-content"
                >
                  Forgot password?
                </button>
                <button
                  type="button"
                  onClick={() => onNavigate('signup')}
                  className="text-brand-400 transition-colors hover:text-brand-300"
                >
                  Create an account
                </button>
              </div>
            </>
          ) : (
            <>
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-soft ring-1 ring-inset ring-brand-ring">
                <MailCheck className="h-4 w-4 text-brand-400" aria-hidden="true" />
              </span>
              <h1 className="mt-5 text-xl font-semibold tracking-tight">Check your email</h1>
              <p className="mt-1.5 text-sm leading-relaxed text-content-muted">
                We sent a code to <span className="text-content">{email}</span>. It expires in 10 minutes.
              </p>

              {devCode && (
                <p className="mt-4 rounded-md border border-line bg-surface-inset px-3 py-2.5 text-xs leading-relaxed text-content-subtle">
                  Development mail driver — the code is <span className="numeral text-brand-400">{devCode}</span>. This
                  never appears in production.
                </p>
              )}

              <form onSubmit={submitPasscode} className="mt-6 space-y-4">
                <div>
                  <label htmlFor="code" className="label">
                    Sign-in code
                  </label>
                  <input
                    id="code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    className="input numeral h-11 text-center text-lg tracking-[0.5em]"
                    autoFocus
                  />
                </div>

                {error && (
                  <p
                    role="alert"
                    className="rounded-md border border-danger-ring bg-danger-soft px-3 py-2.5 text-xs leading-relaxed text-danger-text"
                  >
                    {error}
                  </p>
                )}

                <button type="submit" className="btn btn-primary btn-lg w-full" disabled={busy || code.length < 4}>
                  {busy ? <Spinner /> : <ArrowRight className="h-4 w-4" aria-hidden="true" />}
                  Sign in
                </button>
              </form>

              <div className="mt-6 flex items-center justify-between border-t border-line pt-4 text-sm">
                <button
                  type="button"
                  onClick={() => {
                    setStep('credentials');
                    setCode('');
                    setError(null);
                  }}
                  className="flex items-center gap-1.5 text-content-muted transition-colors hover:text-content"
                >
                  <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
                  Back
                </button>
                <button
                  type="button"
                  onClick={resend}
                  disabled={busy || resendIn > 0}
                  className="text-brand-400 transition-colors hover:text-brand-300 disabled:text-content-faint"
                >
                  {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend code'}
                </button>
              </div>
            </>
          )}
        </div>

        <p className="mt-5 flex items-start gap-2 text-xs leading-relaxed text-content-faint">
          <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          Sessions are held in this tab only and expire after 15 minutes of inactivity.
        </p>
      </div>
    </div>
  );
}
